import { useEffect, useState } from 'react';
import AccessDenied from '@/components/AccessDenied';
import { useAuth } from '@/hooks/useAuth';
import {
  COMPLIANCE_PERMISSIONS,
  hasPermission,
} from '@/lib/compliancePermissions';
import {
  useSensitiveTerms,
  type CreateSensitiveTermDto,
  type SensitiveTermDto,
} from '@/hooks/useCompliance';
import {
  SENSITIVE_TERM_CATEGORY_LABELS,
  SENSITIVE_TERM_STATUS_LABELS,
  RISK_LEVEL_LABELS,
  RISK_LEVEL_LIST,
  formatEnumLabel,
} from '@/lib/enumLabels';

const SENSITIVE_TERM_CATEGORIES: readonly SensitiveTermDto['termCategory'][] = [
  'forbidden',
  'risky',
  'regulated',
];

const SENSITIVE_TERM_STATUSES: readonly SensitiveTermDto['status'][] = [
  'active',
  'inactive',
  'archived',
];

function riskTone(level: SensitiveTermDto['riskLevel']): string {
  if (level === 'critical') return 'bg-red-100 text-red-700';
  if (level === 'high') return 'bg-orange-100 text-orange-700';
  if (level === 'medium') return 'bg-amber-100 text-amber-700';
  return 'bg-emerald-100 text-emerald-700';
}

export default function SensitiveTermsPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, COMPLIANCE_PERMISSIONS.SENSITIVE_TERM_READ);
  const canCreate = hasPermission(permissions, COMPLIANCE_PERMISSIONS.SENSITIVE_TERM_CREATE);
  const canUpdate = hasPermission(permissions, COMPLIANCE_PERMISSIONS.SENSITIVE_TERM_UPDATE);
  const { terms, loading, error, fetchTerms, createTerm, updateTerm } = useSensitiveTerms();

  const [statusFilter, setStatusFilter] = useState<SensitiveTermDto['status'] | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<SensitiveTermDto['termCategory'] | ''>('');
  const [createForm, setCreateForm] = useState<CreateSensitiveTermDto>({
    term: '',
    termCategory: 'forbidden',
    riskLevel: 'medium',
    suggestedReplacement: '',
    notes: '',
  });
  const [editingId, setEditingId] = useState('');
  const [editingReplacement, setEditingReplacement] = useState('');
  const [editingNotes, setEditingNotes] = useState('');
  const [editingStatus, setEditingStatus] = useState<SensitiveTermDto['status']>('active');

  useEffect(() => {
    if (!canRead) {
      return;
    }
    void fetchTerms({
      status: statusFilter || undefined,
      category: categoryFilter || undefined,
    });
  }, [canRead, statusFilter, categoryFilter, fetchTerms]);

  if (!canRead) {
    return <AccessDenied resource="敏感词库" />;
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const result = await createTerm({
      ...createForm,
      suggestedReplacement: createForm.suggestedReplacement || undefined,
      notes: createForm.notes || undefined,
    });
    if (result) {
      setCreateForm({
        term: '',
        termCategory: 'forbidden',
        riskLevel: 'medium',
        suggestedReplacement: '',
        notes: '',
      });
      void fetchTerms({
        status: statusFilter || undefined,
        category: categoryFilter || undefined,
      });
    }
  }

  async function handleUpdate(item: SensitiveTermDto) {
    const result = await updateTerm(item.id, {
      expectedVersion: item.version,
      suggestedReplacement: editingId === item.id ? editingReplacement || undefined : item.suggestedReplacement || undefined,
      notes: editingId === item.id ? editingNotes || undefined : item.notes || undefined,
      status: editingId === item.id ? editingStatus : item.status,
    });
    if (result) {
      setEditingId('');
      void fetchTerms({
        status: statusFilter || undefined,
        category: categoryFilter || undefined,
      });
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">敏感词库</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            维护宣传审核扫描词典，区分禁用词 / 风险词 / 受监管词三类。
          </p>
        </div>
        <div className="flex gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as SensitiveTermDto['status'] | '')}
            className="rounded border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">全部状态</option>
            {SENSITIVE_TERM_STATUSES.map((item) => (
              <option key={item} value={item}>
                {formatEnumLabel(item, SENSITIVE_TERM_STATUS_LABELS, '未知状态')}
              </option>
            ))}
          </select>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value as SensitiveTermDto['termCategory'] | '')}
            className="rounded border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">全部类别</option>
            {SENSITIVE_TERM_CATEGORIES.map((item) => (
              <option key={item} value={item}>
                {formatEnumLabel(item, SENSITIVE_TERM_CATEGORY_LABELS, '未知类别')}
              </option>
            ))}
          </select>
        </div>
      </div>

      {canCreate && (
        <form onSubmit={(e) => { void handleCreate(e); }} className="grid gap-4 rounded-lg border bg-white p-5 shadow-sm lg:grid-cols-2">
          <div className="lg:col-span-2 text-lg font-semibold">新增敏感词</div>
          <input
            className="rounded border border-gray-300 px-3 py-2 text-sm"
            placeholder="敏感词"
            value={createForm.term}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, term: e.target.value }))}
            required
          />
          <select
            className="rounded border border-gray-300 px-3 py-2 text-sm"
            value={createForm.termCategory}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, termCategory: e.target.value as CreateSensitiveTermDto['termCategory'] }))}
          >
            {SENSITIVE_TERM_CATEGORIES.map((item) => (
              <option key={item} value={item}>
                {formatEnumLabel(item, SENSITIVE_TERM_CATEGORY_LABELS, '未知类别')}
              </option>
            ))}
          </select>
          <select
            className="rounded border border-gray-300 px-3 py-2 text-sm"
            value={createForm.riskLevel}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, riskLevel: e.target.value as CreateSensitiveTermDto['riskLevel'] }))}
          >
            {RISK_LEVEL_LIST.map((level) => (
              <option key={level} value={level}>
                {RISK_LEVEL_LABELS[level]}
              </option>
            ))}
          </select>
          <input
            className="rounded border border-gray-300 px-3 py-2 text-sm"
            placeholder="建议替换词"
            value={createForm.suggestedReplacement ?? ''}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, suggestedReplacement: e.target.value }))}
          />
          <textarea
            className="min-h-24 rounded border border-gray-300 px-3 py-2 text-sm lg:col-span-2"
            placeholder="备注"
            value={createForm.notes ?? ''}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, notes: e.target.value }))}
          />
          <div className="lg:col-span-2 flex justify-end">
            <button
              type="submit"
              className="rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              创建词条
            </button>
          </div>
        </form>
      )}

      {error && <div className="rounded bg-red-50 px-4 py-3 text-sm text-red-700">{error.message}</div>}

      <div className="overflow-x-auto rounded-lg border bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">词条</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">类别</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">风险</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">建议替换</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">状态</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">备注</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">加载中…</td>
              </tr>
            ) : terms.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">暂无词条</td>
              </tr>
            ) : (
              terms.map((item) => (
                <tr key={item.id} className="border-t align-top">
                  <td className="px-4 py-3 font-medium">{item.term}</td>
                  <td className="px-4 py-3">{formatEnumLabel(item.termCategory, SENSITIVE_TERM_CATEGORY_LABELS, '未知类别')}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${riskTone(item.riskLevel)}`}>
                      {formatEnumLabel(item.riskLevel, RISK_LEVEL_LABELS, '未知')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {canUpdate ? (
                      <input
                        className="w-full rounded border border-gray-300 px-2 py-1 text-xs"
                        value={editingId === item.id ? editingReplacement : item.suggestedReplacement || ''}
                        onChange={(e) => {
                          setEditingId(item.id);
                          setEditingReplacement(e.target.value);
                        }}
                      />
                    ) : (
                      item.suggestedReplacement || '—'
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {canUpdate ? (
                      <select
                        className="rounded border border-gray-300 px-2 py-1 text-xs"
                        value={editingId === item.id ? editingStatus : item.status}
                        onChange={(e) => {
                          setEditingId(item.id);
                          setEditingStatus(e.target.value as SensitiveTermDto['status']);
                        }}
                      >
                        {SENSITIVE_TERM_STATUSES.map((status) => (
                          <option key={status} value={status}>
                            {formatEnumLabel(status, SENSITIVE_TERM_STATUS_LABELS, '未知状态')}
                          </option>
                        ))}
                      </select>
                    ) : (
                      formatEnumLabel(item.status, SENSITIVE_TERM_STATUS_LABELS, '未知状态')
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {canUpdate ? (
                      <textarea
                        className="min-h-20 w-full rounded border border-gray-300 px-2 py-1 text-xs"
                        value={editingId === item.id ? editingNotes : item.notes || ''}
                        onChange={(e) => {
                          setEditingId(item.id);
                          setEditingNotes(e.target.value);
                        }}
                      />
                    ) : (
                      item.notes || '—'
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {canUpdate ? (
                      <button
                        onClick={() => { void handleUpdate(item); }}
                        className="rounded bg-slate-900 px-2 py-1 text-xs text-white hover:bg-slate-700"
                      >
                        保存
                      </button>
                    ) : (
                      <span className="text-xs text-muted-foreground">只读</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
